import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Key, Copy, RefreshCw, Plug, UserPlus, Eye, EyeOff } from "lucide-react";
import { AdminHeader } from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { api, API, fileUrl } from "@/lib/api";

export default function Settings() {
  const [s, setS] = useState(null);
  const [token, setToken] = useState(null);
  const [fullToken, setFullToken] = useState("");
  const [showToken, setShowToken] = useState(false);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [diag, setDiag] = useState(null);
  const [testing, setTesting] = useState(false);
  const logoRef = useRef();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ email: "", name: "", password: "" });
  const loadAdmins = () => api.get("/auth/admins").then((r) => setAdmins(r.data || [])).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/admin/integration/status").then((r) => setToken(r.data.token_preview)).catch(() => {});
    api.get("/admin/integration/diagnostics").then((r) => setDiag(r.data)).catch(() => {});
  }, []);
  useEffect(() => { if (isOwner) loadAdmins(); }, [isOwner]);

  const createAdmin = async () => {
    if (!newAdmin.email || !newAdmin.password) { toast.error("Email and temporary password are required"); return; }
    try { await api.post("/auth/admins", newAdmin); toast.success("Admin added"); setNewAdmin({ email: "", name: "", password: "" }); loadAdmins(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add admin"); }
  };
  const resetAdminPw = async (a) => {
    const pw = window.prompt(`Enter a new temporary password for ${a.email} (minimum 8 characters):`);
    if (!pw) return;
    try { await api.post(`/auth/admins/${a.id}/password`, { password: pw }); toast.success("Password reset. Share it with them securely."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const toggleActive = async (a) => {
    try { await api.post(`/auth/admins/${a.id}/active`, { active: !a.active }); loadAdmins(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deleteAdmin = async (a) => {
    if (!window.confirm(`Remove admin ${a.email}? They will lose access immediately.`)) return;
    try { await api.delete(`/auth/admins/${a.id}`); toast.success("Admin removed"); loadAdmins(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch { toast.error("Save failed"); } };

  const uploadLogo = async (files) => {
    if (!files?.length) return;
    const fd = new FormData();
    fd.append("file", files[0]);
    try { const r = await api.post("/admin/branding/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setS((x) => ({ ...x, logo_path: r.data.logo_path })); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { if (logoRef.current) logoRef.current.value = ""; }
  };
  const removeLogo = async () => { try { await api.delete("/admin/branding/logo"); setS((x) => ({ ...x, logo_path: null })); toast.success("Logo removed"); } catch { toast.error("Failed"); } };

  const setHour = (i, k, v) => setS((x) => ({ ...x, hours: x.hours.map((h, j) => j === i ? { ...h, [k]: v } : h) }));
  const addHour = () => setS((x) => ({ ...x, hours: [...(x.hours || []), { day: "", value: "" }] }));
  const delHour = (i) => setS((x) => ({ ...x, hours: x.hours.filter((_, j) => j !== i) }));

  const setEmail = (i, v) => setS((x) => ({ ...x, notification_emails: x.notification_emails.map((e, j) => j === i ? v : e) }));
  const addEmail = () => setS((x) => ({ ...x, notification_emails: [...(x.notification_emails || []), ""] }));
  const delEmail = (i) => setS((x) => ({ ...x, notification_emails: x.notification_emails.filter((_, j) => j !== i) }));

  const genToken = async () => {
    if (!window.confirm("Generate a new API token? Any existing Marine Ad AI connection using the old token will stop working.")) return;
    try {
      const r = await api.post("/admin/integration/token");
      setFullToken(r.data.token);
      setShowToken(false);
      setTokenModalOpen(true);
      setToken(r.data.token_preview);
      toast.success("New API token generated");
    } catch { toast.error("Failed"); }
  };
  const closeTokenModal = () => { setTokenModalOpen(false); setFullToken(""); setShowToken(false); };
  const copyFullToken = async () => {
    if (!fullToken) return;
    try { await navigator.clipboard?.writeText(fullToken); toast.success("Token copied to clipboard"); }
    catch { toast.error("Copy failed, select and copy manually"); }
  };
  const apiBase = `${API}/v1/inventory`;

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/integration/test");
      setTestResult(r.data);
      const d = await api.get("/admin/integration/diagnostics");
      setDiag(d.data);
    } catch { toast.error("Test failed"); } finally { setTesting(false); }
  };

  if (!s) return <div className="text-slate-500">Loading...</div>;

  return (
    <div data-testid="admin-settings">
      <AdminHeader title="Settings" subtitle="Dealership contact info, financing, and integrations"
        action={<Button onClick={save} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="save-settings-button"><Save size={16} /> Save</Button>} />

      <Tabs defaultValue="branding">
        <TabsList className="mb-4"><TabsTrigger value="branding" data-testid="tab-branding">Branding</TabsTrigger><TabsTrigger value="contact">Contact & Hours</TabsTrigger><TabsTrigger value="financing">Financing</TabsTrigger><TabsTrigger value="notifications">Lead Emails</TabsTrigger><TabsTrigger value="integrations" data-testid="tab-integrations">Integrations</TabsTrigger>{isOwner && <TabsTrigger value="admins" data-testid="tab-admins">Admins</TabsTrigger>}</TabsList>

        <TabsContent value="branding">
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-xl space-y-4" data-testid="branding-settings">
            <div>
              <h3 className="font-heading font-bold text-lg text-slate-900">MTM Logo</h3>
              <p className="text-sm text-slate-500">Upload your logo (PNG with transparent background works best). It appears in the site header, footer, and admin login. Leave empty to use the default icon.</p>
            </div>
            <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadLogo(e.target.files)} data-testid="logo-file-input" />
            <div className="flex items-center gap-5">
              <div className="h-24 w-48 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 flex items-center justify-center overflow-hidden">
                {s.logo_path ? <img src={fileUrl(s.logo_path, 300)} alt="MTM logo" className="max-h-full max-w-full object-contain" data-testid="logo-preview" /> : <span className="text-xs font-mono text-slate-400">No logo yet</span>}
              </div>
              <div className="flex flex-col gap-2">
                <Button onClick={() => logoRef.current?.click()} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="upload-logo-button">Upload Logo</Button>
                {s.logo_path && <Button variant="outline" onClick={removeLogo} className="border-red-200 text-red-600 hover:bg-red-50 rounded-full" data-testid="remove-logo-button">Remove</Button>}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="contact">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label className="text-xs">Address Line</Label><Input value={s.address_line ?? ""} onChange={(e) => set("address_line", e.target.value)} data-testid="set-address" /></div>
              <div><Label className="text-xs">City, State ZIP</Label><Input value={s.city_state_zip ?? ""} onChange={(e) => set("city_state_zip", e.target.value)} data-testid="set-citystate" /></div>
              <div><Label className="text-xs">Phone</Label><Input value={s.phone ?? ""} onChange={(e) => set("phone", e.target.value)} data-testid="set-phone" /></div>
              <div><Label className="text-xs">Email (placeholder ok)</Label><Input value={s.email ?? ""} onChange={(e) => set("email", e.target.value)} placeholder="not yet confirmed" data-testid="set-email" /></div>
              <div><Label className="text-xs">Facebook URL</Label><Input value={s.social?.facebook ?? ""} onChange={(e) => set("social", { ...s.social, facebook: e.target.value })} /></div>
              <div><Label className="text-xs">Instagram URL</Label><Input value={s.social?.instagram ?? ""} onChange={(e) => set("social", { ...s.social, instagram: e.target.value })} /></div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-2"><Label className="text-xs">Business Hours</Label><Button variant="outline" size="sm" onClick={addHour} className="rounded-full"><Plus size={13} /> Add Row</Button></div>
              <div className="space-y-2">
                {(s.hours || []).map((h, i) => (
                  <div key={i} className="flex gap-2"><Input value={h.day} onChange={(e) => setHour(i, "day", e.target.value)} placeholder="Day(s)" /><Input value={h.value} onChange={(e) => setHour(i, "value", e.target.value)} placeholder="Hours (placeholder ok)" /><Button variant="ghost" size="icon" onClick={() => delHour(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></Button></div>
                ))}
              </div>
            </div>
            <Button onClick={save} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save</Button>
          </div>
        </TabsContent>

        <TabsContent value="financing">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4 max-w-xl">
            <div className="flex items-center justify-between"><Label className="text-sm">Enable Financing CTA</Label><Switch checked={!!s.financing_enabled} onCheckedChange={(v) => set("financing_enabled", v)} data-testid="set-financing-enabled" /></div>
            <div><Label className="text-xs">Financing Provider Name</Label><Input value={s.financing_provider ?? ""} onChange={(e) => set("financing_provider", e.target.value)} data-testid="set-financing-provider" /></div>
            <div><Label className="text-xs">Financing URL</Label><Input value={s.financing_url ?? ""} onChange={(e) => set("financing_url", e.target.value)} placeholder="https://..." data-testid="set-financing-url" /></div>
            <p className="text-xs text-slate-400">All public financing buttons use this admin-managed URL. No provider is hard-coded.</p>
            <Button onClick={save} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save</Button>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-3 max-w-xl">
            <Label className="text-sm">Lead Notification Recipients</Label>
            <p className="text-xs text-slate-400">New leads are emailed to these addresses.</p>
            {(s.notification_emails || []).map((e, i) => (
              <div key={i} className="flex gap-2"><Input type="email" value={e} onChange={(ev) => setEmail(i, ev.target.value)} data-testid={`set-notify-email-${i}`} /><Button variant="ghost" size="icon" onClick={() => delEmail(i)} className="text-slate-400 hover:text-red-600"><Trash2 size={15} /></Button></div>
            ))}
            <Button variant="outline" size="sm" onClick={addEmail} className="rounded-full"><Plus size={13} /> Add Recipient</Button>
            <div><Button onClick={save} className="bg-mtm-purple text-white rounded-full mt-2"><Save size={15} /> Save</Button></div>
          </div>
        </TabsContent>

        <TabsContent value="integrations">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-5 max-w-2xl" data-testid="marine-ad-ai-settings">
            <div className="flex items-center gap-2 text-mtm-purple-deep"><Plug size={20} /><h3 className="font-heading font-bold text-lg">Marine Ad AI</h3></div>
            <p className="text-sm text-slate-500">Expose MTM inventory as a secure REST API that Marine Ad AI can consume. Marine Ad AI functionality is not built into this site.</p>

            <div className="flex items-center justify-between border-t border-slate-100 pt-4"><Label className="text-sm">API Enabled</Label><Switch checked={!!s.integration_enabled} onCheckedChange={(v) => set("integration_enabled", v)} data-testid="integration-enabled-toggle" /></div>

            <div>
              <Label className="text-xs">API Base URL</Label>
              <div className="flex gap-2 mt-1">
                <Input readOnly value={apiBase} className="font-mono text-xs" data-testid="integration-base-url" />
                <Button variant="outline" size="icon" onClick={() => { navigator.clipboard?.writeText(apiBase); toast.success("Copied"); }}><Copy size={15} /></Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Endpoints: GET /v1/inventory (filters: condition, status, brand, updated_since, page, page_size) · GET /v1/inventory/&#123;inventory_id&#125;</p>
            </div>

            <div>
              <Label className="text-xs">Inventory Exposure</Label>
              <Select value={s.integration_exposure || "new_and_used"} onValueChange={(v) => set("integration_exposure", v)}>
                <SelectTrigger className="mt-1" data-testid="integration-exposure"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="new">New Only</SelectItem><SelectItem value="used">Used Only</SelectItem><SelectItem value="new_and_used">New + Used</SelectItem><SelectItem value="all">All Eligible Inventory</SelectItem></SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs">RecJet Dealer ID (provided by Marine Ad AI)</Label>
              <Input value={s.recjet_dealer_id ?? ""} onChange={(e) => set("recjet_dealer_id", e.target.value)} placeholder="Leave blank until Marine Ad AI provides it" className="font-mono text-xs mt-1" data-testid="recjet-dealer-id" />
              <p className="text-[11px] text-slate-400 mt-1">Source provider: <code className="font-mono">mtm_website_api</code>. The RecJet Dealer ID belongs to the dealership; do not invent it.</p>
            </div>

            <div className="border-t border-slate-100 pt-4">
              <Label className="text-xs flex items-center gap-1"><Key size={13} /> API Token</Label>
              <div className="flex gap-2 mt-1 items-center">
                <Input readOnly value={token || "No token generated yet"} className="font-mono text-xs" data-testid="integration-token-preview" />
                <Button variant="outline" onClick={genToken} className="rounded-full whitespace-nowrap" data-testid="generate-token-button"><RefreshCw size={14} /> {token ? "Reset" : "Generate"}</Button>
              </div>
              <p className="text-[11px] text-slate-400 mt-1">Send as header <code className="font-mono">X-API-Key</code>. The full token is shown once at generation.</p>

              <Dialog open={tokenModalOpen} onOpenChange={(o) => { if (!o) closeTokenModal(); }}>
                <DialogContent className="max-w-lg" data-testid="token-reveal-modal">
                  <DialogHeader><DialogTitle className="flex items-center gap-2"><Key size={18} /> Your new API token</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800" data-testid="token-reveal-warning">
                      Copy this token now. For security, it will not be shown again after you leave or refresh this page.
                    </div>
                    <div className="flex gap-2 items-center">
                      <Input readOnly type={showToken ? "text" : "password"} value={fullToken} className="font-mono text-xs" data-testid="token-reveal-value" onFocus={(e) => e.target.select()} />
                      <Button variant="outline" size="icon" onClick={() => setShowToken((v) => !v)} title={showToken ? "Hide" : "Show"} data-testid="token-reveal-toggle">{showToken ? <EyeOff size={15} /> : <Eye size={15} />}</Button>
                    </div>
                    <p className="text-[11px] text-slate-400">Store it in the Marine Ad AI configuration as the <code className="font-mono">X-API-Key</code> value.</p>
                  </div>
                  <DialogFooter>
                    <Button onClick={copyFullToken} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="token-reveal-copy"><Copy size={15} /> Copy Token</Button>
                    <Button variant="outline" onClick={closeTokenModal} className="rounded-full" data-testid="token-reveal-close">Done</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Button onClick={save} className="bg-mtm-purple text-white rounded-full"><Save size={15} /> Save Integration Settings</Button>

            <div className="border-t border-slate-100 pt-4">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Diagnostics</Label>
                <Button variant="outline" onClick={testConnection} disabled={testing} className="rounded-full" data-testid="test-connection-button"><Plug size={14} /> {testing ? "Testing..." : "Test API Connection"}</Button>
              </div>
              {testResult && (
                <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm" data-testid="test-connection-result">
                  <p className="font-semibold text-slate-900">{testResult.available ? "API available" : "API not available (check Enabled toggle and token)"}</p>
                  <p className="text-slate-600">{testResult.record_count} inventory records exposed ({testResult.exposure})</p>
                  <p className="text-[11px] text-slate-400">Last checked: {new Date(testResult.checked_at).toLocaleString()}</p>
                </div>
              )}
              {diag && (
                <div className="mt-3 text-[11px] text-slate-500 space-y-0.5 font-mono">
                  <div>Last successful request: {diag.last_successful_request_at ? new Date(diag.last_successful_request_at).toLocaleString() : "never"}</div>
                  <div>Last auth failure: {diag.last_authentication_failure_at ? new Date(diag.last_authentication_failure_at).toLocaleString() : "none"}</div>
                  <div>Last record count: {diag.last_record_count ?? "unknown"}</div>
                </div>
              )}
            </div>
          </div>
        </TabsContent>

        {isOwner && (
        <TabsContent value="admins">
          <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-3xl space-y-6" data-testid="admins-settings">
            <div>
              <h3 className="font-heading font-bold text-lg text-slate-900">Site Admins</h3>
              <p className="text-sm text-slate-500">As the owner, you can add staff admins who manage inventory and content. Staff admins cannot add or remove other admins. Set a temporary password and share it with them securely; they keep using it until you reset it.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
              <div><Label className="text-xs">Email</Label><Input value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} placeholder="name@example.com" data-testid="new-admin-email" /></div>
              <div><Label className="text-xs">Name</Label><Input value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} placeholder="Full name" data-testid="new-admin-name" /></div>
              <div><Label className="text-xs">Temporary Password</Label><Input value={newAdmin.password} onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })} placeholder="Minimum 8 characters" data-testid="new-admin-password" /></div>
              <Button onClick={createAdmin} className="bg-mtm-purple hover:bg-mtm-purple-deep text-white rounded-full" data-testid="add-admin-button"><UserPlus size={15} /> Add Admin</Button>
            </div>
            <div className="divide-y divide-slate-100 border-t border-slate-100">
              {admins.map((a) => (
                <div key={a.id} className="flex items-center justify-between py-3 gap-4" data-testid={`admin-row-${a.id}`}>
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">{a.name}{a.role === "owner" && <span className="ml-2 text-[10px] font-mono uppercase bg-mtm-purple/10 text-mtm-purple px-1.5 py-0.5 rounded">Owner</span>}{!a.active && <span className="ml-2 text-[10px] font-mono uppercase bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded">Disabled</span>}</p>
                    <p className="text-xs text-slate-500">{a.email}</p>
                  </div>
                  {a.role !== "owner" && (
                    <div className="flex gap-2 shrink-0">
                      <Button variant="outline" size="sm" onClick={() => resetAdminPw(a)} className="rounded-full" data-testid={`reset-pw-${a.id}`}>Reset Password</Button>
                      <Button variant="outline" size="sm" onClick={() => toggleActive(a)} className="rounded-full" data-testid={`toggle-active-${a.id}`}>{a.active ? "Disable" : "Enable"}</Button>
                      <Button variant="outline" size="sm" onClick={() => deleteAdmin(a)} className="rounded-full border-red-200 text-red-600 hover:bg-red-50" data-testid={`delete-admin-${a.id}`}><Trash2 size={14} /></Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
